import { FormattedMessage, useIntl } from "react-intl";

import { useLoggedInHasJira } from "@/components/user-context/helpers/cohorts";
import { wacToken } from "@/libraries/design/foundations/index.ts";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import { DEFAULT_VALUES } from "@/libraries/web-framework/src/localization/values";
import { getLocalizedImage } from "@/localization/localizable-images/paid-lp-jsw/localized-images-jsw-features";
import ImageChecklistSide from "@/patterns/image-checklist-side/src/image-checklist-side";
import { PaidTeamSelectionHeroSection } from "@/templates/paid-landing/sections/paid-hero-section-team-selection-variant.tsx";
import {
    PaidLandingProps,
    PaidLandingTemplate
} from "@/templates/paid-landing/src/paid-landing.template";
import { PaidLoggedInJiraProductTourOverview } from "@/templates/software/jira/logged-in/paid-logged-in/paid-logged-in-jira-product-tour-overview.tsx";
import { BanditsProvider } from "@/utils/anchor-queries/bandits-components.tsx";
import {
    AnchorContentsResponse,
    PaidLandingPersonaTypes
} from "@/utils/anchor-queries/get-anchor-contents";
import {
    BANDITS_PRODUCT,
    BANDITS_USE_CASE
} from "@/utils/anchor-queries/get-bandits-contents";
import { experiments } from "@/utils/experiment/manifest";
import { useExperiment } from "@/utils/experiment/use-experiment";
import { FeatureExposed } from "@/utils/feature-exposed/use-feature-exposed";
import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";

export interface JiraSoftwarePagePaidLpJswProps {
    anchorContents?: AnchorContentsResponse | null;
    persona?: PaidLandingPersonaTypes;
    keyword?: string;
    subAnchor?: string;
    utmMedium?: string;
    utmContent?: string;
    utmCampaign?: string;
}

export const JiraSoftwarePagePaidLpJswFeatures = ({
    keyword,
    subAnchor,
    utmMedium,
    utmContent,
    utmCampaign
}: JiraSoftwarePagePaidLpJswProps) => {
    const intl = useIntl();
    const productHref = useLocalizedHref("/software/jira");
    const loggedInHasJira = useLoggedInHasJira();
    const ctaButtonPropsHref = useLocalizedHref("#start-trial");
    const pageId = "paid-lp-jsw-features";

    const paidLoggedOutHeroSection = (
        <PaidTeamSelectionHeroSection keyword={keyword} />
    );

    const gwpAJContextualBanditsJiraExperiment = useExperiment(
        experiments.gwpAJContextualBanditsJiraBPS
    );
    const isgwpAJContextualBanditsJiraExperimentEnabled =
        gwpAJContextualBanditsJiraExperiment.type === "enrolled" &&
        gwpAJContextualBanditsJiraExperiment.value;

    const gwp9473JSWAnchorUpdates = checkFeatureFlagGate(
        featureFlags.gwp9473JSWAnchorUpdates
    );

    const props: PaidLandingProps = {
        hideNavigationBar: false,
        customHeroSection: paidLoggedOutHeroSection,
        landingType: "jira-software",
        pageId,
        productNavProps: {
            productHref,
            productName: "jira",
            ariaLabel: intl.formatMessage({
                id: "wacJiraSoftwareNavigation.logo-label",
                defaultMessage: "Jira Software logo"
            }),
            dataProductKey: "jiraSoftware",
            showFreeHrefButton: false,
            logoLinkWrapperWidth: "auto"
        },
        heroProps: {
            headerText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswFeatures.hero.header",
                defaultMessage:
                    "Connect every team, task, and project together with Jira"
            }),
            secondaryText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswFeatures.hero.body",
                defaultMessage:
                    "Trusted by 100K+ teams that plan, track, release, and manage world-class software."
            }),
            tertiaryText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswFeatures.heroListHeading",
                defaultMessage:
                    "INCLUDED IN YOUR FREE PLAN: Forever free for up to 10 users"
            }),
            image: {
                src: getLocalizedImage("hero", intl.locale),
                alt: intl.formatMessage({
                    id: "JiraSoftwarePagePaidLpJswFeatures.heroImageAlt",
                    defaultMessage: "Screen capture of a Jira backlog"
                }),
                width: 1134,
                height: 620
            },
            signup: {
                bundle: "jira-software",
                edition: "free",
                featureFlags: {
                    enableDefaultExpandedEmail: false,
                    enableDeferredFullName: true
                }
            }
        },
        tabsProps: {
            dataProductKey: "jiraSoftware-PaidLpJiraFeatures",
            headerText: gwp9473JSWAnchorUpdates
                ? intl.formatMessage({
                      id: "JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.heading",
                      defaultMessage:
                          "Discover AI features that power your teams"
                  })
                : intl.formatMessage({
                      id: "JiraSoftwarePagePaidLpJswFeatures.tabs.heading",
                      defaultMessage:
                          "Discover the features that make Jira so easy to use"
                  }),
            tabStyles: {
                defaultBG: wacToken("color.neutral.light0"),

                defaultText: wacToken("color.extended.blue1000"),
                hoverBG: wacToken("color.neutral.light0"),

                hoverText: wacToken("color.extended.blue1000"),

                activeBG: wacToken("color.extended.blue200"),

                activeText: wacToken("color.extended.blue1000")
            },
            styles: {
                "--lp-tabs-hover": wacToken("color.extended.blue200"),
                "--lp-tabs-unselected": wacToken("color.neutral.light700")
            },
            tabs: gwp9473JSWAnchorUpdates
                ? [
                      {
                          textLabel: intl.formatMessage({
                              id: "JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab1.textLabel",
                              defaultMessage: "Plan"
                          }),
                          id: "plan",
                          component: Plan()
                      },
                      {
                          textLabel: intl.formatMessage({
                              id: "JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab2.textLabel",
                              defaultMessage: "Track"
                          }),
                          id: "track",
                          component: Track()
                      },
                      {
                          textLabel: intl.formatMessage({
                              id: "JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab3.textLabel",
                              defaultMessage: "Collaborate"
                          }),
                          id: "collaborate",
                          component: Collaborate()
                      },
                      {
                          textLabel: intl.formatMessage({
                              id: "JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab4.textLabel",
                              defaultMessage: "Report"
                          }),
                          id: "report",
                          component: Report()
                      }
                  ]
                : [
                      {
                          textLabel: intl.formatMessage({
                              id: "JiraSoftwarePagePaidLpJswFeatures.tabs.tab1.textLabel",
                              defaultMessage: "Boards"
                          }),
                          id: "boards",
                          component: Boards()
                      },
                      {
                          textLabel: intl.formatMessage({
                              id: "JiraSoftwarePagePaidLpJswFeatures.tabs.tab2.textLabel",
                              defaultMessage: "Timeline"
                          }),
                          id: "timeline",
                          component: Timeline()
                      },
                      {
                          textLabel: intl.formatMessage({
                              id: "JiraSoftwarePagePaidLpJswFeatures.tabs.tab3.textLabel",
                              defaultMessage: "Reports"
                          }),
                          id: "reports",
                          component: Reports()
                      },
                      {
                          textLabel: intl.formatMessage({
                              id: "JiraSoftwarePagePaidLpJswFeatures.tabs.tab4.textLabel",
                              defaultMessage: "Automation"
                          }),
                          id: "automation",
                          component: Automation()
                      }
                  ]
        },
        ctaProps: {
            headerText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswFeatures.ctaSection.header",
                defaultMessage:
                    "Move fast, stay aligned, and build better - together"
            }),
            ctaProps: {
                buttonProps: {
                    color: "yellow",
                    url: ctaButtonPropsHref,
                    text: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswFeatures.ctaSection.button",
                        defaultMessage: "Get it free"
                    }),
                    dataEventProps: {
                        "data-event": "clicked",
                        "data-event-container": "ctaSection",
                        "data-event-component": "linkButton",
                        "data-product-key": "jiraSoftware-PaidLpJiraFeatures"
                    }
                }
            },
            backgroundProperties: {
                textColor: wacToken("color.neutral.light0")
            }
        },
        testimonialProps: {
            headerText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswFeatures.socialProof.heading",
                defaultMessage: "Trusted by over 100,000 customers world-wide"
            }),
            logos: [
                {
                    src: "https://dam-cdn.atl.orangelogic.com/AssetLink/5382nib232306760cxko3fu33yr03hc6.svg",
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswFeatures.socialProof.squareAlt",
                        defaultMessage: "Square logo"
                    }),
                    loading: "lazy",
                    width: 162,
                    height: 40
                },
                {
                    src: "https://dam-cdn.atl.orangelogic.com/AssetLink/8sh6x4kh58c7576am3gc70215abr77gu.svg",
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswFeatures.socialProof.ebayAlt",
                        defaultMessage: "eBay logo"
                    }),
                    loading: "lazy",
                    width: 99,
                    height: 41
                },
                {
                    src: "https://dam-cdn.atl.orangelogic.com/AssetLink/7l40fo7ky3ck1752eh0ti31556343388.svg",
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswFeatures.socialProof.spotifyAlt",
                        defaultMessage: "Spotify logo"
                    }),
                    loading: "lazy",
                    width: 163,
                    height: 49
                },
                {
                    src: "https://dam-cdn.atl.orangelogic.com/AssetLink/0atd5rxku6yg42wfhx0b1jdrsusm532i.svg",
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswFeatures.socialProof.ciscoAlt",
                        defaultMessage: "Cisco logo"
                    }),
                    loading: "lazy",
                    width: 92,
                    height: 49
                }
            ]
        },
        alternatingTextAndImageProps: {
            backgroundImage: `linear-gradient(225deg, ${wacToken(
                "color.extended.blue200"
            )} 0%, ${wacToken("color.neutral.light0")} 100%)`,

            horizontalRuleColor: wacToken("color.extended.blue200"),
            imageWidth: "100%",
            imageTextSideRowOnePaddingBelowLargeScreen: "32px 0 16px",
            imageTextSideRowTwoPadding: "16px 0",
            imageTextSideRowThreePaddingBelowLargeScreen: "16px 0 32px",
            //Row 1
            rowOneHeader: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswFeatures.alternatingTextAndImage.rowOneHeader",
                defaultMessage: "Customize how your team’s work flows"
            }),
            rowOneBody: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswFeatures.alternatingTextAndImage.rowOneBody",
                defaultMessage:
                    "Set up, clean up, and automate even the most complicated project workflows."
            }),
            rowOneImageLink: getLocalizedImage("row one", intl.locale),
            rowOneImageAltText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswFeatures.alternatingTextAndImage.rowOneImageAltText",
                defaultMessage: "Jira boards"
            }),
            //Row 2
            rowTwoHeader: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswFeatures.alternatingTextAndImage.secondTextHeader",
                defaultMessage: "Stay on track – even when the track changes"
            }),
            rowTwoBody: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswFeatures.alternatingTextAndImage.secondTextBody",
                defaultMessage:
                    "Use the timeline view to map out the big picture, communicate updates to stakeholders, and ensure your team stays on the same page."
            }),
            rowTwoImageLink: getLocalizedImage("row two", intl.locale),
            rowTwoImageAltText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswFeatures.alternatingTextAndImage.rowTwoImageAltText",
                defaultMessage: "Jira timeline view."
            }),
            //Row 3
            rowThreeHeader: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswFeatures.alternatingTextAndImage.rowThreeHeader",
                defaultMessage: "Bye-bye, spreadsheets"
            }),
            rowThreeBody: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswFeatures.alternatingTextAndImage.rowThreeBody",
                defaultMessage:
                    "Keep every detail of a project centralized in real time so up-to-date info can flow freely across people, teams, and tools."
            }),
            rowThreeImageLink: getLocalizedImage("row three", intl.locale),
            rowThreeImageAltText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswFeatures.alternatingTextAndImage.rowThreeImageAltText",
                defaultMessage: "Spreadsheets"
            })
        }
    };

    return loggedInHasJira ? (
        <FeatureExposed
            experimentKey="jfind-1504-after-d7-get-users-back-to-product"
            group="Test"
        >
            <FeatureExposed
                experimentKey="jfind-get-d1to6-users-back-to-product-for-paid"
                group="variant"
            >
                <FeatureExposed
                    experimentKey="jfind-981-paid-jpto-logged-in-redesign"
                    group="Test"
                >
                    <PaidLoggedInJiraProductTourOverview />
                </FeatureExposed>
            </FeatureExposed>
        </FeatureExposed>
    ) : (
        <BanditsProvider
            product={BANDITS_PRODUCT.JIRA}
            isExperimentEnabled={isgwpAJContextualBanditsJiraExperimentEnabled}
            useCase={BANDITS_USE_CASE.ANCHOR_JOURNEY_JIRA_BPS}
            keyword={keyword}
            subAnchor={subAnchor || ""}
            channel={utmMedium || "paid-search"}
            utmContent={utmContent || ""}
            utmCampaign={utmCampaign || ""}
        >
            <PaidLandingTemplate {...props} />
        </BanditsProvider>
    );
};

/* Tabs */

const Boards = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswFeatures.tabs.tab1.heading",
        defaultMessage: "Powerful agile boards"
    });
    const listItems = [
        <FormattedMessage
            key="tabs.tab1.item1"
            id="JiraSoftwarePagePaidLpJswFeatures.tabs.tab1.item1"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Scrum boards:</strong> Scrum boards help agile teams break large, complex projects into manageable pieces of work so focused teams ship faster."
        />,
        <FormattedMessage
            key="tabs.tab1.item2"
            id="JiraSoftwarePagePaidLpJswFeatures.tabs.tab1.item2"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Kanban boards:</strong> Agile and DevOps teams can use
            flexible kanban boards to visualize workflows, limit
            work-in-progress, and maximize efficiency as a team. Templates make
            it easy to get started quickly and customize as you go."
        />,
        <FormattedMessage
            key="tabs.tab1.item3"
            id="JiraSoftwarePagePaidLpJswFeatures.tabs.tab1.item3"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Choose your own adventure:</strong> Jira is
            flexible enough to mold to your team’s own unique way of working,
            whether it is Scrum, Kanban, or something in between."
        />
    ];
    const imageSrc = getLocalizedImage("boards", intl.locale);
    const imageAlt = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswFeatures.tabs.tab1.imageAlt",
        defaultMessage: "Jira boards"
    });

    return (
        <ImageChecklistSide
            title={title}
            listItems={listItems}
            imageSrc={imageSrc}
            imageAlt={imageAlt}
        />
    );
};

const Timeline = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswFeatures.tabs.tab2.heading",
        defaultMessage: "Timeline"
    });

    const listItems = [
        <FormattedMessage
            key="tabs.tab2.item1"
            id="JiraSoftwarePagePaidLpJswFeatures.tabs.tab2.item1"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Keep teams aligned:</strong> Give your team the visibility
            they need to make fast and informed decisions while staying aligned
            with the bigger goals."
        />,
        <FormattedMessage
            key="tabs.tab2.item2"
            id="JiraSoftwarePagePaidLpJswFeatures.tabs.tab2.item2"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Track the big picture:</strong> Plan and track how you’re
            making progress on the big picture for a single team."
        />,
        <FormattedMessage
            key="tabs.tab2.item3"
            id="JiraSoftwarePagePaidLpJswFeatures.tabs.tab2.item3"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Get ahead of dependencies:</strong> Visualize dependencies
            within your team to account for them when making plans."
        />,
        <FormattedMessage
            key="tabs.tab2.item4"
            id="JiraSoftwarePagePaidLpJswFeatures.tabs.tab2.item4"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Plan with team capacity in mind (Premium only):</strong>
            Ensure your teams have bandwidth to complete the work they’ve
            scoped. See multiple teams' capacity on a sprint-by-sprint
            basis."
        />
    ];

    const imageSrc = getLocalizedImage("timeline", intl.locale);
    const imageAlt = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswFeatures.tabs.tab2.imageAlt",
        defaultMessage: "Jira timeline"
    });

    return (
        <ImageChecklistSide
            title={title}
            listItems={listItems}
            imageSrc={imageSrc}
            imageAlt={imageAlt}
        />
    );
};

const Reports = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswFeatures.tabs.tab3.heading",
        defaultMessage: "Reports and insights"
    });

    const listItems = [
        <FormattedMessage
            key="tabs.tab3.item1"
            id="JiraSoftwarePagePaidLpJswFeatures.tabs.tab3.item1"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Ready to go reporting:</strong> Out-of-the-box reports and
            dashboards in Jira offer critical insights within the
            context of your work to ensure your teams are always up to date and
            set up for success."
        />,
        <FormattedMessage
            key="tabs.tab3.item2"
            id="JiraSoftwarePagePaidLpJswFeatures.tabs.tab3.item2"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Sprint reporting:</strong> Determine where your team is
            overcommitted to reduce excessive scope creep and better understand
            completed work in each sprint."
        />,
        <FormattedMessage
            key="tabs.tab3.item3"
            id="JiraSoftwarePagePaidLpJswFeatures.tabs.tab3.item3"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Burndown chart:</strong> Track work towards sprint goals to
            manage progress and respond accordingly."
        />,
        <FormattedMessage
            key="tabs.tab3.item4"
            id="JiraSoftwarePagePaidLpJswFeatures.tabs.tab3.item4"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Release Burndown:</strong> Track and monitor the projected
            release date for versions and take action if work is falling behind
            schedule."
        />
    ];

    const imageSrc = getLocalizedImage("reports", intl.locale);
    const imageAlt = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswFeatures.tabs.tab3.imageAlt",
        defaultMessage: "Jira reports"
    });

    return (
        <ImageChecklistSide
            title={title}
            listItems={listItems}
            imageSrc={imageSrc}
            imageAlt={imageAlt}
        />
    );
};

const Automation = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswFeatures.tabs.tab4.heading",
        defaultMessage: "Automation"
    });
    const listItems = [
        <FormattedMessage
            key="tabs.tab4.item1"
            id="JiraSoftwarePagePaidLpJswFeatures.tabs.tab4.item1"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Drag and drop automation:</strong> Focus on the important
            things. Let automation do the rest. Powerful, yet simple - Jira
            automation is actually fun to use."
        />,
        <FormattedMessage
            key="tabs.tab4.item2"
            id="JiraSoftwarePagePaidLpJswFeatures.tabs.tab4.item2"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Auto-assign issues:</strong> When an issue is raised without
            an assignee, auto-assign to whoever created it so nothing falls
            through the cracks."
        />,
        <FormattedMessage
            key="tabs.tab4.item3"
            id="JiraSoftwarePagePaidLpJswFeatures.tabs.tab4.item3"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Sync work:</strong> When an epic is marked as 'done' move
            all of its stories to 'done' also."
        />,
        <FormattedMessage
            key="tabs.tab4.item4"
            id="JiraSoftwarePagePaidLpJswFeatures.tabs.tab4.item4"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Daily Slack summary:</strong> Send a daily Slack message
            with a list of issues still open in the Sprint."
        />,
        <FormattedMessage
            key="tabs.tab4.item5"
            id="JiraSoftwarePagePaidLpJswFeatures.tabs.tab4.item5"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Auto-close old support issues:</strong> If a customer has
            not responded to an issue in 5 days, close the issue and leave a
            comment."
        />
    ];

    const imageSrc = getLocalizedImage("automation", intl.locale);
    const imageAlt = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswFeatures.tabs.tab4.imageAlt",
        defaultMessage: "Jira automation"
    });

    return (
        <ImageChecklistSide
            title={title}
            listItems={listItems}
            imageSrc={imageSrc}
            imageAlt={imageAlt}
        />
    );
};

/* New Tabs for GWP-9473 */

export const Plan = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab1.heading",
        defaultMessage: "Kick off with clarity"
    });
    const listItems = [
        <FormattedMessage
            key="tabs.gwp9473.tab1.item1"
            id="JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab1.item1"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Align work to goals:</strong> Connect work to company impact and collaborate with cross-functional partners."
        />,
        <FormattedMessage
            key="tabs.gwp9473.tab1.item2"
            id="JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab1.item2"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Break down projects:</strong> Go from big picture to automatically assigned tasks with Rovo AI."
        />,
        <FormattedMessage
            key="tabs.gwp9473.tab1.item3"
            id="JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab1.item3"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Get the context you need:</strong> Use Rovo to find relevant resources and rich data from across your apps."
        />
    ];

    const imageSrc =
        "https://dam-cdn.atl.orangelogic.com/AssetLink/y43424xo5d312uq63es504lre561n4hf.webp";
    const imageAlt = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab1.imageAlt",
        defaultMessage: "Jira plan view"
    });

    return (
        <ImageChecklistSide
            title={title}
            listItems={listItems}
            imageSrc={imageSrc}
            imageAlt={imageAlt}
        />
    );
};

export const Track = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab2.heading",
        defaultMessage: "Stay up to date"
    });

    const listItems = [
        <FormattedMessage
            key="tabs.gwp9473.tab2.item1"
            id="JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab2.item1"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Track your work, your way:</strong> See projects in a board, list, timeline, or calendar view."
        />,
        <FormattedMessage
            key="tabs.gwp9473.tab2.item2"
            id="JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab2.item2"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Manage risks and dependencies:</strong> Get status updates and any blockers from AI agents."
        />,
        <FormattedMessage
            key="tabs.gwp9473.tab2.item3"
            id="JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab2.item3"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Remove work around work:</strong> Use Rovo AI to automate tedious tasks."
        />
    ];

    const imageSrc =
        "https://dam-cdn.atl.orangelogic.com/AssetLink/33884gu02a10x77i0282w08okk31o4c3.webp";
    const imageAlt = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab2.imageAlt",
        defaultMessage: "Jira track view"
    });

    return (
        <ImageChecklistSide
            title={title}
            listItems={listItems}
            imageSrc={imageSrc}
            imageAlt={imageAlt}
        />
    );
};

export const Collaborate = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab3.heading",
        defaultMessage: "Bring teams together"
    });

    const listItems = [
        <FormattedMessage
            key="tabs.gwp9473.tab3.item1"
            id="JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab3.item1"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Automate workflows:</strong> Use Rovo AI and everyday language to build any workflow."
        />,
        <FormattedMessage
            key="tabs.gwp9473.tab3.item2"
            id="JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab3.item2"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Streamline requests:</strong> Get all the details you need and share your progress with forms."
        />,
        <FormattedMessage
            key="tabs.gwp9473.tab3.item3"
            id="JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab3.item3"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Keep everyone aligned:</strong> Provide real-time status updates and use Rovo to identify risks."
        />
    ];

    const imageSrc =
        "https://dam-cdn.atl.orangelogic.com/AssetLink/py7ek04olrn273137jbptehj24b21d5p.webp";
    const imageAlt = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab3.imageAlt",
        defaultMessage: "Jira collaborate view"
    });

    return (
        <ImageChecklistSide
            title={title}
            listItems={listItems}
            imageSrc={imageSrc}
            imageAlt={imageAlt}
        />
    );
};

export const Report = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab4.heading",
        defaultMessage: "Inform your decisions"
    });

    const listItems = [
        <FormattedMessage
            key="tabs.gwp9473.tab4.item1"
            id="JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab4.item1"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Track progress against goals:</strong> Get visibility into how your team's work contributes to company goals and progress."
        />,
        <FormattedMessage
            key="tabs.gwp9473.tab4.item2"
            id="JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab4.item2"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>See performance insights:</strong> Understand your teams with cycle time reports, burn down charts, and capacity plans."
        />,
        <FormattedMessage
            key="tabs.gwp9473.tab4.item3"
            id="JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab4.item3"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Iterate with AI:</strong> Use Rovo's insights to identify recurring themes and priorities."
        />
    ];

    const imageSrc =
        "https://dam-cdn.atl.orangelogic.com/AssetLink/0d4wi87lq61r3551o21ij30mm5l03buq.webp";
    const imageAlt = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab4.imageAlt",
        defaultMessage: "Jira report view"
    });

    return (
        <ImageChecklistSide
            title={title}
            listItems={listItems}
            imageSrc={imageSrc}
            imageAlt={imageAlt}
        />
    );
};
